import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthContext } from '../contexts/AuthContext';
import LoginForm from '../components/auth/LoginForm';
import OtpChallenge from '../components/auth/OtpChallenge';
import { LoginCredentials } from '../types/auth';
import { useTheme } from '../contexts/ThemeContext';
import { getSubdomain, navigateToTenant, validateTenantSubdomain } from '../utils/tenantUtils';
import { userService } from '../services/userService';
import { toast } from 'react-hot-toast';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, captcha, isCaptchaVerified, awaitingOtp } = useAuthContext();
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [error, setError] = React.useState('');
  const [isAutoLoggingIn, setIsAutoLoggingIn] = React.useState(false);

  // Check for pending login credentials when on a subdomain
  React.useEffect(() => {
    const attemptAutoLogin = async () => {
      const currentSubdomain = getSubdomain();
      const pendingLoginStr = sessionStorage.getItem('pendingLogin');

      if (currentSubdomain && pendingLoginStr) {
        try {
          const pendingLogin = JSON.parse(pendingLoginStr);
          setIsAutoLoggingIn(true);

          // Store the subdomain as tenant ID in local storage
          localStorage.setItem('tenantId', currentSubdomain);

          // Add a small delay to ensure the tenant context is properly set up
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Attempt to login with the stored credentials
          try {
            const result = await login(pendingLogin);
            // Clear the pending login data
            sessionStorage.removeItem('pendingLogin');
            if ((result as any)?.twoFactorRequired) {
              setIsAutoLoggingIn(false);
              return;
            }
            navigate('/dashboard');
          } catch (err) {
            setError(t('pages.login.automaticLoginFailed'));
            setIsAutoLoggingIn(false);
          }
        } catch (e) {
          console.error('Error parsing pending login data:', e);
          sessionStorage.removeItem('pendingLogin');
          setIsAutoLoggingIn(false);
        }
      }
    };

    attemptAutoLogin();
  }, [login, navigate]);

  const handleLogin = async (credentials: LoginCredentials) => {
    try {
      setError('');

      // Check for tenant in query parameter (for IP address scenarios)
      const queryParams = new URLSearchParams(location.search);
      const tenantFromQuery = queryParams.get('tenant');

      const currentSubdomain = getSubdomain() || tenantFromQuery;

      // If we're not on a subdomain and the user provided one, validate and navigate to that subdomain
      if (!currentSubdomain && credentials.subdomain) {
        setIsAutoLoggingIn(true); // Show loading state while validating

        // Validate that the tenant subdomain exists
        const tenantInfo = await validateTenantSubdomain(credentials.subdomain);

        if (!tenantInfo) {
          setError(t('auth.invalidSubdomain', { subdomain: credentials.subdomain }));
          setIsAutoLoggingIn(false);
          return;
        }

        // Store credentials temporarily in sessionStorage to use after redirect
        credentials = {
          email: credentials.email,
          password: credentials.password,
          tenantId: tenantInfo.id,
          subdomain: credentials.subdomain
        };
      }
      // If we're on a subdomain or have a tenant query parameter but no subdomain was provided in the form,
      // make sure the user knows they're logging into a specific organization
      else if (currentSubdomain) {
        // Store the tenant ID in localStorage before login attempt
        // This ensures it's available for the API request
        localStorage.setItem('tenantId', currentSubdomain);

        // Add the tenant to the credentials for the login request
        credentials.subdomain = currentSubdomain;
      }

      // Normal login flow
      try {
        const result = await login(credentials);
        // If the server requires an email OTP, show the OTP step instead of navigating.
        if ((result as any)?.twoFactorRequired) {
          setIsAutoLoggingIn(false);
          return;
        }
        navigate('/dashboard');
      } catch (loginError: any) {
        // Handle specific error cases
        if (loginError.message?.includes('Account setup incomplete')) {
          setError(t('pages.login.incompleteSetup'));
        } else if (currentSubdomain && loginError.message?.includes('Invalid credentials')) {
          setError(t('pages.login.invalidCredentialsForOrg', { subdomain: currentSubdomain }));
        } else {
          setError(t('pages.login.invalidCredentials'));
        }
        setIsAutoLoggingIn(false);
      }
    } catch (error: any) {
      setError(error.message || t('pages.login.unexpectedError'));
      setIsAutoLoggingIn(false);
    }
  };

  const handleCaptcha = async (token: string) => {
    try {
      setError('');
      await captcha(token);
    } catch (error: any) {
      setError(error.message || t('pages.login.unexpectedError'));
      setIsAutoLoggingIn(false);
    }
  };

  const handleForgotPassword = async (email: string, subdomain: string) => {
    try {
      const response = await userService.forgotPassword(email, subdomain);
      toast.success(response.message || 'Password reset instructions sent to your email');
    } catch (error: any) {
      toast.error(error.message || 'Failed to send password reset instructions');
      throw error;
    }
  };

  return (
    <div
      className={`login-page min-h-screen ${
        theme === 'dark'
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900'
          : 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50'
      }`}
    >
      {/* Scrollable container */}
      <div className="min-h-screen overflow-y-auto overflow-x-hidden">
        <div className="flex items-center justify-center py-8 px-4 sm:py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-md space-y-8">
            {/* Main card */}
            <div
              className={`relative p-6 sm:p-8 rounded-xl shadow-xl backdrop-blur-sm border transition-all duration-300 ${
                theme === 'dark'
                  ? 'bg-gray-800/90 shadow-gray-900/50 border-gray-700/50'
                  : 'bg-white/95 shadow-blue-100/50 border-gray-200/50'
              }`}
            >
              {/* Header */}
              <div className="text-center">
                <div className="mx-auto h-14 w-14 mb-6">
                  <img
                    src="/favicon.png"
                    alt={t('common.appLogo')}
                    className="h-full w-full object-contain drop-shadow-sm"
                  />
                </div>
                <h1
                  className={`text-2xl sm:text-3xl font-bold tracking-tight ${
                    theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
                  }`}
                >
                  {isAutoLoggingIn ? t('pages.login.loggingIn') : t('pages.login.welcomeBack')}
                </h1>
                <p
                  className={`mt-3 text-sm sm:text-base ${
                    theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  {isAutoLoggingIn
                    ? t('pages.login.connecting')
                    : t('pages.login.signInToContinue')}
                </p>
              </div>

              {/* Error message */}
              {error && (
                <div
                  className={`rounded-lg p-4 animate-[slideIn_0.3s_ease-out] border ${
                    theme === 'dark'
                      ? 'bg-red-900/20 border-red-800/30 backdrop-blur-sm'
                      : 'bg-red-50 border-red-200'
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0">
                      <svg
                        className={`h-5 w-5 mt-0.5 ${
                          theme === 'dark' ? 'text-red-400' : 'text-red-500'
                        }`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="ml-3 flex-1">
                      <p
                        className={`text-sm font-medium ${
                          theme === 'dark' ? 'text-red-200' : 'text-red-800'
                        }`}
                      >
                        {error}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Loading state or form */}
              {isAutoLoggingIn ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="animate-pulse w-6 h-6 bg-blue-600 rounded-full"></div>
                    </div>
                  </div>
                  <p
                    className={`mt-6 text-base font-medium ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                    }`}
                  >
                    {t('pages.login.connecting')}
                  </p>
                </div>
              ) : awaitingOtp ? (
                <div className="space-y-6">
                  <OtpChallenge onVerified={() => navigate('/dashboard')} />
                </div>
              ) : (
                <div className="space-y-6">
                  <LoginForm
                    onSubmit={handleLogin}
                    onCaptcha={handleCaptcha}
                    isCaptchaVerified={isCaptchaVerified}
                    onForgotPassword={handleForgotPassword}
                  />
                </div>
              )}

              {/* Footer */}
              <div
                className={`mt-8 pt-6 border-t text-center ${
                  theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {t('pages.login.newToApp')}{' '}
                  <a
                    href="/register"
                    className={`font-semibold transition-colors duration-200 ${
                      theme === 'dark'
                        ? 'text-blue-400 hover:text-blue-300'
                        : 'text-blue-600 hover:text-blue-700'
                    }`}
                  >
                    {t('pages.login.registerOrg')}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
